import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { obituaryDetailsService } from '../../../../services/obituaryDetailsInfo';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('ObituaryDetailsWriterInput');

type ObituaryDetailsWriterInputScreenRouteProp = RouteProp<RootStackParamList, 'ObituaryDetailsWriterInput'>;

export const ObituaryDetailsWriterInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<ObituaryDetailsWriterInputScreenRouteProp>();

  const { obituaryDetailsData, isEditing } = route.params || {};

  // Writer information states
  const [writerName, setWriterName] = useState(obituaryDetailsData?.writerName || '');
  const [relationship, setRelationship] = useState(obituaryDetailsData?.relationship || '');
  const [contactPhone, setContactPhone] = useState(obituaryDetailsData?.contactPhone || '');
  const [contactEmail, setContactEmail] = useState(obituaryDetailsData?.contactEmail || '');
  const [backupWriter, setBackupWriter] = useState(obituaryDetailsData?.backupWriter || '');
  const [backupContact, setBackupContact] = useState(obituaryDetailsData?.backupContact || '');
  const [writingStyle, setWritingStyle] = useState(obituaryDetailsData?.writingStyle || '');
  const [collaborationPreference, setCollaborationPreference] = useState(obituaryDetailsData?.collaborationPreference || '');
  const [deadlinePreference, setDeadlinePreference] = useState(obituaryDetailsData?.deadlinePreference || '');
  const [sourceMaterials, setSourceMaterials] = useState(obituaryDetailsData?.sourceMaterials || '');
  const [interviewContacts, setInterviewContacts] = useState(obituaryDetailsData?.interviewContacts || '');
  const [specialInstructions, setSpecialInstructions] = useState(obituaryDetailsData?.specialInstructions || '');
  const [writerNotes, setWriterNotes] = useState(obituaryDetailsData?.writerNotes || '');

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!writerName.trim()) {
      Alert.alert('Error', 'Please enter at least a writer name');
      return;
    }

    try {
      setSaving(true);

      const obituaryUpdateData = {
        writerName: writerName.trim(),
        relationship: relationship.trim(),
        contactPhone: contactPhone.trim(),
        contactEmail: contactEmail.trim(),
        backupWriter: backupWriter.trim(),
        backupContact: backupContact.trim(),
        writingStyle: writingStyle.trim(),
        collaborationPreference: collaborationPreference.trim(),
        deadlinePreference: deadlinePreference.trim(),
        sourceMaterials: sourceMaterials.trim(),
        interviewContacts: interviewContacts.trim(),
        specialInstructions: specialInstructions.trim(),
        writerNotes: writerNotes.trim(),
        obituaryPreference: 'yes', // Setting default preference
      };

      let savedObituary;

      if (isEditing && obituaryDetailsData?.id) {
        // Update existing record
        savedObituary = await obituaryDetailsService.updateObituaryDetails(
          obituaryDetailsData.id,
          obituaryUpdateData
        );
        logger.debug('Obituary writer info updated successfully with ID:', savedObituary.id);

        // Navigate to lock/unlock screen to allow re-selection of lock/unlock
        navigation.navigate('ObituaryDetailsLockUnlock', { obituaryDetailsId: savedObituary.id });
      } else {
        // Create new record
        savedObituary = await obituaryDetailsService.createObituaryDetails(obituaryUpdateData);
        logger.debug('Obituary writer info saved successfully with ID:', savedObituary.id);

        // Navigate to lock/unlock screen with the ID
        navigation.navigate('ObituaryDetailsLockUnlock', { obituaryDetailsId: savedObituary.id });
      }
    } catch (error) {
      logger.error('Error saving obituary writer info:', error);
      Alert.alert('Error', 'Failed to save obituary writer information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          Add Your Obituary Writer Information
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Obituary Writer Name:</Text>
            <TextInput
              style={styles.input}
              value={writerName}
              onChangeText={setWriterName}
              placeholder="Enter writer's full name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Relationship to You:</Text>
            <TextInput
              style={styles.input}
              value={relationship}
              onChangeText={setRelationship}
              placeholder="e.g., Son, Daughter, Friend"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Writer's Phone Number:</Text>
            <TextInput
              style={styles.input}
              value={contactPhone}
              onChangeText={setContactPhone}
              placeholder="Enter phone number"
              placeholderTextColor="#A9A9A9"
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Writer's Email:</Text>
            <TextInput
              style={styles.input}
              value={contactEmail}
              onChangeText={setContactEmail}
              placeholder="Enter email address"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Backup Writer Name:</Text>
            <TextInput
              style={styles.input}
              value={backupWriter}
              onChangeText={setBackupWriter}
              placeholder="Enter backup writer's name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Backup Writer Contact:</Text>
            <TextInput
              style={styles.input}
              value={backupContact}
              onChangeText={setBackupContact}
              placeholder="Phone or email"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Writing Style:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={writingStyle}
              onChangeText={setWritingStyle}
              placeholder="Describe preferred writing style"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Collaboration Preference:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={collaborationPreference}
              onChangeText={setCollaborationPreference}
              placeholder="Should the writer work alone or with family?"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Timeline/Deadline:</Text>
            <TextInput
              style={styles.input}
              value={deadlinePreference}
              onChangeText={setDeadlinePreference}
              placeholder="When should the obituary be prepared?"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Source Materials:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={sourceMaterials}
              onChangeText={setSourceMaterials}
              placeholder="Photos, letters, documents to reference"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Interview Contacts:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={interviewContacts}
              onChangeText={setInterviewContacts}
              placeholder="People to interview for information"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={specialInstructions}
              onChangeText={setSpecialInstructions}
              placeholder="Any special instructions for the writer"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Additional Notes:</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={writerNotes}
              onChangeText={setWriterNotes}
              placeholder="Any other information or notes"
              placeholderTextColor="#A9A9A9"
              multiline={true}
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.submitButtonContainer}>
            <TouchableOpacity
              style={[styles.saveButton, saving && { opacity: 0.7 }]}
              onPress={handleSave}
              disabled={saving}
            >
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
   backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
    paddingBottom: ms(120),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  textArea: {
    minHeight: s(80),
    textAlignVertical: 'top',
  },
  submitButtonContainer: {
    alignItems: 'center',
    marginTop: ms(20),
    marginBottom: ms(40),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    paddingHorizontal: ms(60),
    alignItems: 'center',
    marginBottom: ms(120),
    minWidth: s(200),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
});

export default ObituaryDetailsWriterInputScreen;
